import React, { useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

import {
  selectAnalyticsStartDate,
  selectAnalyticsEndDate,
  selectProductCodes,
  selectDateRange,
  selectProductAnalyticsIds,
} from "../../store/product/ProductSelectors";
import { loadProductAnalytics } from "../../store/product/ProductActions";
import { selectOverlay } from "../../store/overlay/OverlaySelectors";
import { selectCurrentStoreId } from "../../store/store-list/StoreListSelectors";
import { OVERLAY_TABS } from "../../utils/Constants";
import {
  selectSortingRule,
  selectSortingRulesList,
  selectIsFetchingProductIds,
  selectIsProductListFetching,
} from "../../store/product-list/ProductListSelectors";
import { BusinessRulesListDetails } from "../../store/product-list/ProductListTypes";
import { calculateStartEndDate } from "../../utils/ProductUtil";
import { selectSystemGeneratedRules } from "../../store/businessRules-list/BusinessRuleslistSelectors";
import { selectCurrentCategory } from "../../store/category/CategorySelectors";

const AnalyticsOverlayLabel = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const overlay = useSelector(selectOverlay);
  const startDate = useSelector(selectAnalyticsStartDate);
  const endDate = useSelector(selectAnalyticsEndDate);
  const productCodes = useSelector(selectProductCodes);
  const productAnalyticsIds = useSelector(selectProductAnalyticsIds);
  const storeId = useSelector(selectCurrentStoreId) || "";
  const selectedDateRange = useSelector(selectDateRange);
  const sortingRulesList = useSelector(selectSortingRulesList);
  const systemGeneratedRulesList = useSelector(selectSystemGeneratedRules);
  const allSortingRulesList = [
    ...systemGeneratedRulesList,
    ...sortingRulesList,
  ];

  const selectedRule = useSelector(selectSortingRule);
  const isFetchingProductIds = useSelector(selectIsFetchingProductIds);
  const isProductListFetching = useSelector(selectIsProductListFetching);
  const currentCategory = useSelector(selectCurrentCategory);

  // Create stable keys for array comparison (compare content, not reference)
  const productAnalyticsIdsKey = useMemo(() => {
    return JSON.stringify([...productAnalyticsIds].sort());
  }, [productAnalyticsIds]);

  // Track last dispatched payload to prevent duplicates
  const lastPayloadRef = useRef<string>("");
  const lastCategoryRef = useRef<string | null>(null);

  // Reset when category changes
  useEffect(() => {
    if (lastCategoryRef.current !== currentCategory) {
      lastPayloadRef.current = "";
      lastCategoryRef.current = currentCategory;
    }
  }, [currentCategory]);

  useEffect(
    () => {
      if (
        overlay === OVERLAY_TABS.ANALYTICS_OVERLAY &&
        !isFetchingProductIds && // Wait until product IDs finish loading
        !isProductListFetching && // Wait until product list finishes loading
        productCodes.length > 0
      ) {
        const sortingRule = allSortingRulesList?.find(
          (rule) => rule.ruleId === selectedRule,
        );
        let analyticsPayload;
        
        if (sortingRule) {
          const { startDate, endDate } = calculateStartEndDate(
            sortingRule as BusinessRulesListDetails,
          );
          if (startDate && endDate) {
            analyticsPayload = {
              startDate: startDate,
              endDate: endDate,
              productCodes: productAnalyticsIds,
            };
          }
        } else {
          const fromDate = selectedDateRange.startDate;
          const toDate = selectedDateRange.endDate;
          if (fromDate !== startDate || toDate !== endDate) {
            analyticsPayload = {
              startDate: fromDate,
              endDate: toDate,
              productCodes: productAnalyticsIds,
            };
          }
        }
        if (analyticsPayload) {
          const sortedPayload = {
            ...analyticsPayload,
            productCodes: [...analyticsPayload.productCodes].sort(),
          };
          const payloadKey = JSON.stringify(sortedPayload);
          
          // Only dispatch if this exact payload hasn't been sent
          if (lastPayloadRef.current !== payloadKey) {
            lastPayloadRef.current = payloadKey;
            dispatch(loadProductAnalytics(analyticsPayload));
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      productAnalyticsIdsKey,
      overlay,
      startDate,
      endDate,
      dispatch,
      storeId,
      selectedDateRange,
      selectedRule,
      allSortingRulesList,
      isFetchingProductIds,
      isProductListFetching,
    ],
  );

  return (
    <>
      {intl.formatMessage({
        id: "overlayTabs.analyticsLabel",
        defaultMessage: "Analytics",
      })}
    </>
  );
};

export default React.memo(AnalyticsOverlayLabel);
